import moment from 'moment';
import { AfterViewInit, Component, ElementRef, EventEmitter, OnInit, Output, TemplateRef, ViewChild, HostListener } from '@angular/core';
import { debounceTime, fromEvent, Subscription } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, NavigationEnd } from '@angular/router';
import { USER_CLAIM, USER_PROFILE } from '@shared/service/constants.services';
import { ApolloUserService } from '@core/graphql/apolloUser.service';
import { jwtDecode } from 'jwt-decode';
import { SharedService } from '@shared/service/sharedservice';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-parent-header',
  templateUrl: './parent-header.component.html',
  styleUrl: './parent-header.component.scss'
})
export class ParentHeaderComponent implements OnInit {
  userDetail: any;
  userClaim: any;
  userId: any;
  @Output() displayChange = new EventEmitter<boolean>();
  loading: boolean = false;
  isMobileMenuOpen: boolean = false;
  isMobile: boolean = false;
  alertProperties = { type: '', message: '' }
  showAlert: boolean = false;

  userRoles: any[] = [];
  currentRole: string = 'Parent'; // Default to Parent
  switchToRole: string = 'Admin'; // Default switch role
  hasAdminAndParentRole: boolean = false;

  selectedYear: number;
  selectedSchool: string = '';
  schoolList: string[] = [];
  profileImageBase64: string = '';

  @ViewChild('headerPopup', { static: true })
  headerPopup!: TemplateRef<object>;

  constructor(private formBuilder: FormBuilder, private router: Router, private sharedService: SharedService, private userService: ApolloUserService,) {
    this.selectedYear = new Date().getFullYear();
  }

  ngOnInit(): void {
    this.sharedService.schoolList$.subscribe((schoolList) => {
      this.schoolList = schoolList;
    });
  
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      )
      .subscribe((event: NavigationEnd) => {
        if (event.url === '/parent/mykids') {
          this.sharedService.showCard(true);
        } else {
          this.sharedService.showCard(false);
        }
      });
    this.checkIsMobile();
    this.loadRoles();
    this.loadProfile();
  }

  loadProfile() {
    const userProfile = localStorage.getItem("userProfile");
    if (userProfile) {
      const parsedProfile = JSON.parse(userProfile);
      this.userId = parsedProfile.id;
      if (this.userId) {
        this.userDetail = null;
        this.getUserById(this.userId);
      }
    } else {
      this.router.navigate(['/login']);
    }
  }

  getUserById(id: any) {
    this.userService.getUserById(id).subscribe({
      next: (result: any) => {
        this.userDetail = result;
        if (result.profilePictureBase64) {
          this.profileImageBase64 = `data:image/png;base64,${result.profilePictureBase64}`;
        } else {
          this.profileImageBase64 = 'assets/images/user-profile.jpg';
        }
      }
    });
  }

  loadRoles() {
    const rolesString = localStorage.getItem('userRoles');
    if (!rolesString) {
      return;
    }

    this.userRoles = JSON.parse(rolesString);

    const adminRole = this.userRoles.find((role) => role.name === 'Admin');
    const parentRole = this.userRoles.find((role) => role.name === 'Parent');

    this.hasAdminAndParentRole = !!adminRole && !!parentRole;

    const availableRoles: string[] = [];
    if (parentRole) {
      availableRoles.push('Parent');
    }
    if (adminRole) {
      availableRoles.push('Admin');
    }

    const storedRole = this.sharedService.getActiveRole();
    this.currentRole = availableRoles.includes(storedRole) ? storedRole : (availableRoles[0] ?? 'Parent');
    this.switchToRole = availableRoles.find(role => role !== this.currentRole) ?? this.currentRole;
  }

  toggleRole(): void {
    const previousRole = this.currentRole;
    this.currentRole = this.switchToRole;
    this.switchToRole = previousRole;

    // Update activeRole in localStorage via RoleService
    this.sharedService.setCurrentRole(this.currentRole);

    // Redirect based on the current role
    if (this.currentRole === 'Admin') {
      this.sharedService.showCard(false);
      this.router.navigate([this.sharedService.buildRoleBasedUrl('user/listing')], { queryParams: { tab: 'user' } });
    } else {
      this.sharedService.showCard(true);
      this.router.navigate([this.sharedService.buildRoleBasedUrl('mykids')]);
    }
  }

  signOut() {
    this.loading = true;
    localStorage.clear();
    this.sharedService.setCurrentRole('');
    localStorage.removeItem('userProfile');
    localStorage.removeItem('userClaim');
    localStorage.removeItem('userSecret');
    localStorage.removeItem('userRoles');
    this.router.navigate(['/login'], { replaceUrl: true });
    this.loading = false;
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
  }

  @HostListener('window:resize', [])
  onResize() {
    this.checkIsMobile();
  }

  checkIsMobile() {
    this.isMobile = window.innerWidth <= 767;
  }

  applySelectedSchool(): void {
    if (this.selectedSchool) {
      console.log('Selected School:', this.selectedSchool);
      this.displayAlert('success', `Switched to ${this.selectedSchool}`);
    } else {
      this.displayAlert('danger', 'Please select a school to apply.');
    }
  }

  get parentRegistrationLink(): string {
    return this.sharedService.buildRoleBasedUrlFor('Parent', 'student/registration');
  }

  get parentHomeLink(): string {
    return this.sharedService.buildRoleBasedUrlFor('Parent', 'dashboard');
  }

  get parentMyKidsLink(): string {
    return this.sharedService.buildRoleBasedUrlFor('Parent', 'mykids');
  }

  get parentChatLink(): string {
    return this.sharedService.buildRoleBasedUrlFor('Parent', 'chat');
  }

  displayAlert(type: string, message: string) {
    this.showAlert = true;
    this.alertProperties = { type: type, message: message }
    window.setTimeout(() => {
      this.showAlert = false;
    }, 5000)
  }
}
